// import React, { Component, Fragment } from 'react'
import React, { useState, Fragment, useEffect } from "react"
import { Route, Routes } from "react-router-dom"
import { v4 as uuid } from "uuid"
import { Segment } from "semantic-ui-react"

// import AuthenticatedRoute from './components/shared/AuthenticatedRoute'
import AutoDismissAlert from "./components/shared/AutoDismissAlert/AutoDismissAlert"
// import Header from "./components/shared/Header"
import RequireAuth from "./components/shared/RequireAuth"
import Home from "./components/Home"
import SignUp from "./components/auth/SignUp"
import SignIn from "./components/auth/SignIn"
import SignOut from "./components/auth/SignOut"
import ChangePassword from "./components/auth/ChangePassword"
import User from "./components/auth/User"
import UserPosts from "./components/UserPosts"
import {
	getStoredUser,
	setStoredUser,
	clearStoredUser,
	installAuthInterceptor,
	registerUnauthorizedHandler,
} from "./api/client"
import { getSessions } from "./api/auth"
import { Helmet } from "react-helmet"
import "semantic-ui-css/semantic.min.css"

installAuthInterceptor()

const App = () => {
	// restore the signed-in user before the first render so a reload doesn't
	// flash the signed-out UI
	const [user, setUser] = useState(getStoredUser)
	const [msgAlerts, setMsgAlerts] = useState([])

	const appHeight = () => {
		const doc = document.documentElement
		doc.style.setProperty("--app-height", `${window.innerHeight}px`)
	}
	window.addEventListener("resize", appHeight)
	appHeight()

	// keep localStorage in step with the user state
	useEffect(() => {
		if (user !== null) {
			setStoredUser(user)
		} else {
			clearStoredUser()
		}
	}, [user])

	// the interceptor needs a way to drop React state when the server rejects
	// our token, not just clear localStorage
	useEffect(() => {
		registerUnauthorizedHandler(() => setUser(null))
	}, [])

	// a stored token can now be revoked from another device or expire on its
	// own, so check it once on boot rather than trusting it until the user
	// happens to click something
	useEffect(() => {
		const stored = getStoredUser()
		if (stored) {
			// a 401 is handled by the interceptor, which clears the session
			getSessions(stored).catch(() => {})
		}
	}, [])

	const clearUser = () => {
		setUser(null)
	}

	const deleteAlert = (id) => {
		setMsgAlerts((prevState) => {
			return prevState.filter((msg) => msg.id !== id)
		})
	}

	const msgAlert = ({ heading, message, variant }) => {
		const id = uuid()
		setMsgAlerts(() => {
			return [{ heading, message, variant, id }]
		})
	}

	return (
		<Segment inverted vertical textAlign="center">
			<Fragment>
				{/* <Header user={user} /> */}

				<Routes>
					<Route
						path="/"
						element={<Home msgAlert={msgAlert} user={user} />}
					/>
					<Route
						path="/my-posts"
						element={<UserPosts msgAlert={msgAlert} user={user} />}
					/>
					<Route
						path="/user"
						element={
							<User
								msgAlert={msgAlert}
								user={user}
								setUser={setUser}
							/>
						}
					/>
					<Route
						path="/sign-up"
						element={
							<SignUp msgAlert={msgAlert} setUser={setUser} />
						}
					/>
					<Route
						path="/sign-in"
						element={
							<SignIn
								msgAlert={msgAlert}
								setUser={setUser}
								user={user}
							/>
						}
					/>
					<Route
						path="/sign-out"
						element={
							<RequireAuth user={user}>
								<SignOut
									msgAlert={msgAlert}
									clearUser={clearUser}
									user={user}
								/>
							</RequireAuth>
						}
					/>
					<Route
						path="/change-password"
						element={
							<RequireAuth user={user}>
								<ChangePassword
									msgAlert={msgAlert}
									user={user}
								/>
							</RequireAuth>
						}
					/>
				</Routes>
				<Helmet>
					<link
						rel="apple-touch-icon"
						sizes="180x180"
						href="%PUBLIC_URL%/tinyweather-icon-touch.png"
						/>
				</Helmet>
						{msgAlerts.map((msgAlert) => (
							<AutoDismissAlert
								key={msgAlert.id}
								heading={msgAlert.heading}
								variant={msgAlert.variant}
								message={msgAlert.message}
								id={msgAlert.id}
								deleteAlert={deleteAlert}
							/>
						))}
			</Fragment>
		</Segment>
	)
}

export default App
